import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import * as mcRulesSelectors from 'data/mcRules/mcRulesSelectors';
import type { MCRuleMessage } from 'data/mcRules/mcRulesTypes';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import QButton, { QButtonVariantEnum } from 'components/QButton';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import * as mcRulesActions from 'data/mcRules/mcRulesActions';
import { removeDialog } from 'data/rootUi/actions';
import useEvent from 'hooks/useEvent';
import tracker from 'utils/tracker';

const useStyles = makeStyles((theme: Object) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  remindButton: {
    position: 'absolute',
    left: theme.spacing(1),
  },
  dialogContentPadding: {
    paddingTop: 0,
    paddingBottom: 20,
  },
  iframe: {
    opacity: 1, // this is needed to defeat the janky iframe opacity override on body (for userSnap)
  },
}));

const MCPDialog = (props) => {
  const classes = useStyles();
  const message: MCRuleMessage = useSelector((state) => mcRulesSelectors.getMessageById(state, props.id)) || {};
  const [open, setOpen] = useState(true);
  const [remindMeLater, setRemindMeLater] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    tracker.track(tracker.events.memberCommunicationDisplayMessage, {
      ruleId: message?.ruleId,
      ruleName: message?.ruleName,
      messageId: message?.id,
      hook: message?.hook,
      reDisplayCount: message?.reDisplayCount,
      variantName: message?.variantName,
    });
  }, []);

  useEvent('message', useCallback((event) => {
    if (event?.data?.type === 'mcp' && event?.data?.action === 'dismiss') {
      onClose();
    }
  }, [onClose]));

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  function onExit() {
    if (message && message.id) {
      dispatch(mcRulesActions.mcMessagesTrack({
        id: message.id,
        isRemindMeLater: message.canUserDismiss ? remindMeLater : undefined,
        reDisplayCount: message.reDisplayCount,
        ruleContext: message.ruleContext,
      }));
      tracker.track(tracker.events.memberCommunicationDismissMessage, {
        ruleContext: message?.ruleContext,
        type: message?.type,
        reDisplayCount: message?.reDisplayCount,
        ruleId: message?.ruleId,
        ruleName: message?.ruleName,
        hook: message?.hook,
        variantName: message?.variantName,
        didUserOptOut: remindMeLater ? 0 : 1,
        displayNameOptOut: message?.ruleGroup?.displayName,
      });
    }
    dispatch(removeDialog(props.dialogId));
  }

  return (
    <Dialog
      open={Boolean(message && open)}
      aria-labelledby="message-from-app"
      onClose={onClose}
      onExit={onExit}
    >
      <DialogTitle id="important-message-dialog-title">
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
        {props.title}
      </DialogTitle>
      <DialogContent className={classes.dialogContentPadding}>
        <iframe
          className={classes.iframe}
          title={message.id}
          width={message.contentWidth}
          height={message.contentHeight}
          srcDoc={message.messageText ? message.messageText : undefined}
          src={message.messageUrl ? message.messageUrl : undefined}
        />
      </DialogContent>
      <DialogActions>
        {message && message.canUserDismiss &&
        <FormControlLabel
          className={classes.remindButton}
          checked={remindMeLater}
          control={<Checkbox color="primary" />}
          label="Remind me later"
          labelPlacement="end"
          onChange={(event, checked) => setRemindMeLater(checked)}
        />
        }
        <QButton
          type="submit"
          variant={QButtonVariantEnum.CONTAINED}
          onClick={onClose}
        >
          Close
        </QButton>
      </DialogActions>
    </Dialog>
  );
};

MCPDialog.propTypes = {
  title: PropTypes.string,
  id: PropTypes.string,
  dialogId: PropTypes.string,
};

export default MCPDialog;
